import React from 'react';
import { LinkIcon, AlertCircle, FileText, InfoIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import type { ArxivMetadata } from '@/lib/api';
import { ArxivLicense } from '@/types/license';
import { LicenseStatusDisplay } from './LicenseStatusDisplay';
import { DuplicateNotification } from './DuplicateNotification';

interface ExternalUrlInputProps {
  externalUrl: string;
  setExternalUrl: (url: string) => void;
  validationStatus: 'idle' | 'validating' | 'valid' | 'invalid';
  validationMessage: string;
  validationErrors: string[];
  validateExternalUrl: () => Promise<void>;
  metadata?: ArxivMetadata | null;
  license?: ArxivLicense;
  licenseCompliant?: boolean;
  licenseValidationError?: string;
  duplicateInfo?: {
    isDuplicate: boolean;
    existingPaper?: {
      id: string;
      title: string;
      external_url: string;
    };
  } | null;
}

export const ExternalUrlInput: React.FC<ExternalUrlInputProps> = ({
  externalUrl,
  setExternalUrl,
  validationStatus,
  validationMessage,
  validationErrors,
  validateExternalUrl,
  metadata,
  license,
  licenseCompliant,
  licenseValidationError,
  duplicateInfo
}) => {
  const navigate = useNavigate();

  const handleViewPaper = (paperId: string) => {
    navigate(`/paper/${paperId}`);
  };
  
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExternalUrl(e.target.value);
  };
  
  const handleValidateClick = async () => {
    await validateExternalUrl();
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="external-url">External Paper URL</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Enter a valid arXiv paper URL (e.g., https://arxiv.org/abs/2304.12345)</p>
                <p className="text-xs mt-1">This will automatically fetch the paper's metadata from the arXiv API.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex gap-2">
          <Input 
            id="external-url" 
            placeholder="https://arxiv.org/abs/2304.12345"
            value={externalUrl}
            onChange={handleUrlChange}
            className="h-12"
            data-track="external_url_input"
            data-track-category="upload"
          />
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleValidateClick}
            disabled={validationStatus === 'validating' || !externalUrl.trim()}
            className="h-12 whitespace-nowrap"
            data-track="validate_arxiv_url"
            data-track-category="upload"
          >
            {validationStatus === 'validating' ? 'Fetching...' : 'Fetch Metadata'}
          </Button>
        </div>
      </div>
      
      <div className="rounded-lg border p-4">
        <div className="flex items-center gap-3 mb-4">
          <LinkIcon className="h-5 w-5 text-muted-foreground" />
          <div>
            <h3 className="text-sm font-medium">External Paper Link</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Enter the URL to a paper on an external repository (e.g., arXiv) to automatically fetch its metadata
            </p>
          </div>
        </div>
        
        {validationStatus === 'valid' && (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 px-3 py-2 rounded-md text-sm flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500"></div>
              <span>{validationMessage || 'Paper metadata fetched successfully'}</span>
            </div>
            
            {metadata && (
              <div className="bg-background rounded-md border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium">Fetched Paper Metadata</h4>
                </div>
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-medium text-muted-foreground">Title:</p>
                  <p className="text-sm">{metadata.title}</p>
                </div>
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-medium text-muted-foreground">Authors:</p>
                  <p className="text-sm">{metadata.authors.join(', ')}</p>
                </div>
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-medium text-muted-foreground">Category:</p>
                  <p className="text-sm">{metadata.categories && metadata.categories.length > 0 ? metadata.categories[0] : ''}</p>
                </div>
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-medium text-muted-foreground">Abstract Preview:</p>
                  <p className="text-sm line-clamp-2">{metadata.abstract}</p>
                </div>
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-medium text-muted-foreground">Source URL:</p>
                  <p className="text-sm">{externalUrl}</p>
                </div>
              </div>
            )}
            
            {/* License Status Display */}
            <LicenseStatusDisplay
              license={license}
              isCompliant={licenseCompliant}
              isLoading={validationStatus === 'validating' as any}
              error={licenseValidationError}
            />
          </div>
        )}
        
        {/* Duplicate notification */}
        {duplicateInfo?.isDuplicate && duplicateInfo.existingPaper && (
          <DuplicateNotification
            existingPaper={duplicateInfo.existingPaper}
            onViewPaper={handleViewPaper}
          />
        )}
        
        {validationStatus === 'invalid' && !duplicateInfo?.isDuplicate && (
          <div className="space-y-2">
            <div className="bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-3 py-2 rounded-md text-sm flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-red-500"></div>
              <span>{validationMessage || 'Validation failed'}</span>
            </div>
            
            {validationErrors.length > 0 && (
              <Alert variant="destructive" className="bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Validation Errors</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        
        {validationStatus === 'validating' && (
          <div className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
            <span>Fetching paper metadata from arXiv API...</span>
          </div>
        )}
        
        {validationStatus === 'idle' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-gray-400"></div>
            <span>Enter an arXiv URL and click "Fetch Metadata" to automatically populate paper details</span>
          </div>
        )}
      </div>
    </div>
  );
};
